/**
 * Psych-social intent — Ben Petty voice note 2026-05-05 (afternoon, note 3).
 *
 * Four curated psych-social intents at grassroots, replacing the free-text
 * `Session.psych_social_intent` field that comes from §02 / §04.3. Free text
 * at grassroots produced too-vague answers ("teamwork", "confidence") that
 * didn't translate into design choices. The constrained enum gives the AI
 * something concrete to design AROUND — every grassroots session must commit
 * to one of these four intents and the drill choices have to reflect it.
 *
 * Why these four (Ben note 3 verbatim — preserved here as the canonical list):
 *   - confidence_on_ball       — "do they take a touch under pressure rather than panic?"
 *   - decisions_under_pressure — "do they pick the right pass when there's a defender close?"
 *   - resilience_after_mistakes— "do they react well after they lose the ball or miss?"
 *   - focus                    — "are they paying attention through the whole session?"
 *
 * Academy tier keeps the existing free-text field — academy coaches have the
 * vocabulary to articulate richer intents and the team profile is built up
 * over time.
 *
 * Storage stays as a free-text column (`Session.psych_social_intent`). At
 * grassroots tier, the UI surfaces a chip picker that writes the enum value
 * into that column; at academy tier, free text is kept. `parseGrassrootsIntent`
 * narrows the column value back to the enum on read where the tier requires it.
 */

import type { CoachingTier } from './phaseOfPlay';

export const GRASSROOTS_PSYCH_SOCIAL_INTENTS = [
  'confidence_on_ball',
  'decisions_under_pressure',
  'resilience_after_mistakes',
  'focus',
] as const;

export type GrassrootsPsychSocialIntent = typeof GRASSROOTS_PSYCH_SOCIAL_INTENTS[number];

export interface GrassrootsPsychSocialIntentMeta {
  id: GrassrootsPsychSocialIntent;
  /** Coach-facing label for chip picker. */
  label: string;
  /** Ben's framing — the question a coach can ask themselves at the end
   *  of the session to know if the intent landed. Surfaced under the chip
   *  as a short helper line + reused in coach_focus_questions output. */
  description: string;
  /** Single-character glyph for compact chips. */
  glyph: string;
  /** Distinct colour family — keeps the chip readable next to phase
   *  + style chips on a session card. */
  colorHex: string;
}

export const GRASSROOTS_PSYCH_SOCIAL_INTENT_META: Record<
  GrassrootsPsychSocialIntent,
  GrassrootsPsychSocialIntentMeta
> = {
  confidence_on_ball: {
    id: 'confidence_on_ball',
    label: 'Confidence on the ball',
    description: 'Do they take a touch under pressure rather than panic?',
    glyph: '◆',
    colorHex: '#F39C12',
  },
  decisions_under_pressure: {
    id: 'decisions_under_pressure',
    label: 'Decisions under pressure',
    description: "Do they pick the right pass when there's a defender close?",
    glyph: '◇',
    colorHex: '#E67E22',
  },
  resilience_after_mistakes: {
    id: 'resilience_after_mistakes',
    label: 'Resilience after mistakes',
    description: 'Do they react well after they lose the ball or miss?',
    glyph: '↻',
    colorHex: '#C0392B',
  },
  focus: {
    id: 'focus',
    label: 'Focus',
    description: 'Are they paying attention through the whole session?',
    glyph: '◉',
    colorHex: '#2980B9',
  },
};

/** Validate a candidate psych-social intent value (e.g. coming from an API
 *  client or AI tool input) against the grassroots enum. Returns the
 *  validated value or null when the input isn't one of the four canonical
 *  values. */
export function parseGrassrootsIntent(value: unknown): GrassrootsPsychSocialIntent | null {
  if (typeof value !== 'string') return null;
  return (GRASSROOTS_PSYCH_SOCIAL_INTENTS as readonly string[]).includes(value)
    ? (value as GrassrootsPsychSocialIntent)
    : null;
}

/** Tier-aware validator for the `Session.psych_social_intent` column.
 *
 *  - Grassroots: must be one of the four enum values. Returns the enum
 *    value or `{ ok: false, reason: 'not_in_grassroots_enum' }`.
 *  - Academy:    accepts any non-empty trimmed string. Returns the
 *    string or `{ ok: false, reason: 'empty' }`.
 *
 *  Used in two places: validating coach input on the session form, and
 *  validating AI-generated output before saving. */
export function validatePsychSocialIntent(
  value: string | null | undefined,
  tier: CoachingTier,
): { ok: true; value: string } | { ok: false; reason: 'empty' | 'not_in_grassroots_enum' } {
  const trimmed = (value ?? '').trim();
  if (trimmed.length === 0) return { ok: false, reason: 'empty' };

  if (tier === 'grassroots') {
    return parseGrassrootsIntent(trimmed)
      ? { ok: true, value: trimmed }
      : { ok: false, reason: 'not_in_grassroots_enum' };
  }

  // Academy — any non-empty string is acceptable.
  return { ok: true, value: trimmed };
}

/** Heuristic — given a free-text psych-social intent (often the legacy
 *  Session.psych_social_intent column from before the enum existed), classify
 *  it to one of the four grassroots intents. Returns null when nothing
 *  matches confidently — caller can fall back to a Haiku call or surface a
 *  chip picker for the coach to disambiguate. */
export function inferGrassrootsIntent(text: string): GrassrootsPsychSocialIntent | null {
  const t = text.toLowerCase();

  // Resilience first — "after they lose" / "after a mistake" is unambiguous.
  if (/\b(resilien|after (a )?mistake|bounce.?back|after\s+\w+\s+(lose|loss|miss)|reset after)\b/.test(t)) {
    return 'resilience_after_mistakes';
  }

  // Decisions under pressure — pick / choose / right pass language with a
  // pressure cue.
  if (/\b(decision|choose|pick.+pass|right pass|right option|under (the )?pressure)\b/.test(t)) {
    return 'decisions_under_pressure';
  }

  // Confidence on the ball — touch / take a touch / on the ball / not panic.
  if (/\b(confidence|on the ball|take a touch|don.?t panic|composure|first touch under)\b/.test(t)) {
    return 'confidence_on_ball';
  }

  // Focus — concentration, attention, switched on.
  if (/\b(focus|concentration|attention|switched on|stay sharp|engaged)\b/.test(t)) {
    return 'focus';
  }

  return null;
}
